import React from "react";
import { withTranslation } from "react-i18next";
import { motion } from "framer-motion";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorCode: null };
  }

  static getDerivedStateFromError(error) {
    // Just return that there's an error, no need to differentiate types in UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log chunk loading errors
    if (
      error.name === "ChunkLoadError" ||
      (error.message && error.message.includes("Loading chunk"))
    ) {
      console.error("Chunk loading error caught by ErrorBoundary:", error);
      console.error("Error info:", errorInfo);

      // Log additional details if available from our retry mechanism
      if (error.chunkName) {
        console.error(`Failed chunk: ${error.chunkName}`);
      }
      if (error.retryCount !== undefined) {
        console.error(`Retry attempts made: ${error.retryCount}`);
      }
    }
  }

  componentDidMount() {
    // Add global error listeners to catch chunk loading errors and other runtime errors
    this.handleGlobalError = this.handleGlobalError.bind(this);
    this.handleUnhandledRejection = this.handleUnhandledRejection.bind(this);

    window.addEventListener("error", this.handleGlobalError);
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  componentWillUnmount() {
    // Clean up event listeners
    window.removeEventListener("error", this.handleGlobalError);
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  handleGlobalError = (event) => {
    // Catch any global errors including chunk loading errors
    if (event.error) {
      console.log("Global error caught by ErrorBoundary:", event.error);

      // Check if it's a chunk loading error or any other error
      if (
        event.error.name === "ChunkLoadError" ||
        event.error.message?.includes("Loading chunk") ||
        event.error.message?.includes("ChunkLoadError") ||
        event.error.message?.includes("Failed to fetch") ||
        event.error.message?.includes("NetworkError") ||
        event.error.message?.includes("preventDefault is not a function") ||
        event.error.message?.includes("TypeError") ||
        event.error.message?.includes("ReferenceError")
      ) {
        console.log("Error detected, setting error state");
        this.setState({ hasError: true });
        return false; // Prevent default error handling
      }
    }

    // Also catch errors without event.error (some errors don't have it)
    if (event.message) {
      console.log(
        "Global error message caught by ErrorBoundary:",
        event.message
      );

      if (
        event.message.includes("preventDefault") ||
        event.message.includes("TypeError") ||
        event.message.includes("ReferenceError") ||
        event.message.includes("Loading chunk") ||
        event.message.includes("ChunkLoadError")
      ) {
        console.log("Error message detected, setting error state");
        this.setState({ hasError: true });
        return false;
      }
    }
  };

  handleUnhandledRejection = (event) => {
    // Catch unhandled promise rejections (common with chunk loading)
    if (event.reason) {
      console.log("Unhandled rejection caught by ErrorBoundary:", event.reason);

      if (
        event.reason.name === "ChunkLoadError" ||
        event.reason.message?.includes("Loading chunk") ||
        event.reason.message?.includes("ChunkLoadError") ||
        event.reason.message?.includes("preventDefault is not a function") ||
        event.reason.message?.includes("TypeError") ||
        event.reason.message?.includes("ReferenceError")
      ) {
        console.log("Error rejection detected, setting error state");
        this.setState({ hasError: true });
        event.preventDefault(); // Prevent default error handling
      }
    }
  };

  render() {
    if (this.state.hasError) {
      const { i18n } = this.props;
      const currentLanguage = i18n?.language || "en";

      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-200 via-white to-red-400 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gradient-to-br from-red-100/20 via-transparent to-orange-100/20"></div>

          <motion.div
            transition={{ duration: 0.6, delay: 0.1 }}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.95 }}
            className="relative z-10 flex flex-col items-center max-w-md mx-auto text-center"
          >
            <div className="w-full">
              {/* Animated Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mb-8"
              >
                <div className="relative mx-auto w-32 h-32 flex items-center justify-center">
                  {/* Pulsing background circle */}
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 bg-gradient-to-br from-red-100 to-orange-100 rounded-full opacity-50"
                  />

                  {/* Error Icon */}
                  <motion.svg
                    width="80"
                    height="80"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="relative z-10"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                  >
                    <motion.path
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ duration: 1.2, delay: 0.6 }}
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.84308 3.80211C9.8718 2.6007 10.8862 2 12 2C13.1138 2 14.1282 2.6007 16.1569 3.80211L16.8431 4.20846C18.8718 5.40987 19.8862 6.01057 20.4431 7C21 7.98943 21 9.19084 21 11.5937V12.4063C21 14.8092 21 16.0106 20.4431 17C19.8862 17.9894 18.8718 18.5901 16.8431 19.7915L16.1569 20.1979C14.1282 21.3993 13.1138 22 12 22C10.8862 22 9.8718 21.3993 7.84308 20.1979L7.15692 19.7915C5.1282 18.5901 4.11384 17.9894 3.55692 17C3 16.0106 3 14.8092 3 12.4063V11.5937C3 9.19084 3 7.98943 3.55692 7C4.11384 6.01057 5.1282 5.40987 7.15692 4.20846L7.84308 3.80211ZM13 16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16ZM12 6.25C12.4142 6.25 12.75 6.58579 12.75 7V13C12.75 13.4142 12.4142 13.75 12 13.75C11.5858 13.75 11.25 13.4142 11.25 13V7C11.25 6.58579 11.5858 6.25 12 6.25Z"
                      fill="#DC2626"
                    />
                  </motion.svg>
                </div>
              </motion.div>

              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-2xl font-semibold text-gray-500 mb-3"
              >
                {currentLanguage === "ar"
                  ? "حدث خطأ ما"
                  : "Something went wrong"}
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-gray-400 mb-6 leading-relaxed"
              >
                {currentLanguage === "ar"
                  ? "يرجى إعادة تحميل الصفحة لإعادة المحاولة."
                  : "Please refresh the page to try again."}
              </motion.p>

              {/* Refresh button */}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  this.setState({ hasError: false });
                  window.location.reload();
                }}
                className="w-full py-3 px-6 rounded-xl font-semibold text-white transition-all duration-300 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                {/* Refresh Icon */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C9.25022 4 6.82447 5.38734 5.38451 7.50024"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 7.5L5.5 7.5L5.5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {currentLanguage === "ar"
                  ? "إعادة تحميل الصفحة"
                  : "Refresh Page"}
              </motion.button>
            </div>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrap the ErrorBoundary component with the withTranslation HOC
export default withTranslation()(ErrorBoundary);
